/**
 * @file sanitizeProviderData.ts
 * @description Security utilities for sanitizing providerData to prevent XSS (Task 27.5)
 * 
 * PRD Reference: §8 Security
 */

/**
 * List of allowed URL protocols for QR codes
 */
const ALLOWED_QR_PROTOCOLS = ['https:', 'http:', 'data:'];

/**
 * List of dangerous URL protocols that could execute code
 */
const DANGEROUS_PROTOCOLS = ['javascript:', 'vbscript:', 'data:text/html'];

/**
 * Validate if a QR code URL is safe to use
 * 
 * Allows:
 * - https:// URLs
 * - http:// URLs (for development)
 * - data:image/* URLs (for inline QR codes)
 * 
 * Rejects:
 * - javascript: URLs
 * - vbscript: URLs
 * - data:text/html URLs
 * - Empty/null/undefined URLs
 * 
 * @param url - URL to validate
 * @returns true if URL is safe, false otherwise
 */
export const isValidQrCodeUrl = (url: string | null | undefined): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Decode URL to catch encoded attacks like java%73cript:
  let decodedUrl: string;
  try {
    decodedUrl = decodeURIComponent(url).toLowerCase();
  } catch {
    // If decoding fails, use original URL
    decodedUrl = url.toLowerCase();
  }

  // Check for dangerous protocols
  for (const protocol of DANGEROUS_PROTOCOLS) {
    if (decodedUrl.startsWith(protocol)) {
      return false;
    }
  }

  // Check for javascript: with any encoding tricks
  if (decodedUrl.includes('javascript:') || decodedUrl.includes('vbscript:')) {
    return false;
  }

  // Parse URL to check protocol
  try {
    // Handle data: URLs specially
    if (url.startsWith('data:')) {
      // Only allow data:image/* URLs
      return url.startsWith('data:image/');
    }

    const parsedUrl = new URL(url);
    return ALLOWED_QR_PROTOCOLS.includes(parsedUrl.protocol);
  } catch {
    // If URL parsing fails, reject it
    return false;
  }
};

/**
 * Sanitize a blockchain address by removing potentially dangerous characters
 * 
 * Preserves:
 * - Alphanumeric characters (a-z, A-Z, 0-9)
 * - Ethereum address prefix (0x)
 * 
 * Removes:
 * - HTML tags
 * - Special characters that could be used for injection
 * - Whitespace (trimmed)
 * 
 * @param address - Address to sanitize
 * @returns Sanitized address
 */
export const sanitizeAddress = (address: string | null | undefined): string => {
  if (!address || typeof address !== 'string') {
    return '';
  }

  // Trim whitespace
  const trimmed = address.trim();

  // Ethereum addresses: 0x + hex chars — extract valid hex portion only
  const ethAddressMatch = trimmed.match(/^(0x[a-fA-F0-9]+)/);
  if (ethAddressMatch) {
    return ethAddressMatch[1];
  }

  // Solana base58 addresses: 32-44 chars from base58 alphabet (excludes 0/O/I/l)
  // XSS-safe: whitelist-only regex, no HTML or script characters pass through
  const solanaAddressMatch = trimmed.match(/^([1-9A-HJ-NP-Za-km-z]{32,44})/);
  if (solanaAddressMatch) {
    return solanaAddressMatch[1];
  }

  // Fallback: alphanumeric strip (safer than previous hex-only strip for unknown formats)
  return trimmed.replace(/[^a-zA-Z0-9]/g, '');
};

/**
 * Provider data structure from payment API
 */
export interface ProviderData {
  qrCodeUrl?: string;
  toAddress?: string;
  network?: string;
  chainId?: number;
  tokenSymbol?: string;
  amount?: string;
  [key: string]: unknown;
}

/**
 * Sanitize provider data to prevent XSS attacks
 * 
 * @param providerData - Raw provider data from API
 * @returns Sanitized provider data safe for rendering
 */
export const sanitizeProviderData = (
  providerData: ProviderData | null | undefined
): ProviderData => {
  if (!providerData || typeof providerData !== 'object') {
    return {};
  }

  const sanitized: ProviderData = { ...providerData };

  // Sanitize qrCodeUrl
  if ('qrCodeUrl' in sanitized) {
    sanitized.qrCodeUrl = isValidQrCodeUrl(sanitized.qrCodeUrl)
      ? sanitized.qrCodeUrl
      : '';
  }

  // Sanitize toAddress
  if ('toAddress' in sanitized && typeof sanitized.toAddress === 'string') {
    sanitized.toAddress = sanitizeAddress(sanitized.toAddress);
  }

  return sanitized;
};
